import React, { useState } from 'react';
import api from '../services/api';
import './GetQuote.css';

function GetQuote() {
  const [formData, setFormData] = useState({
    // Contact Details
    fullName: '',
    companyName: '',
    phoneNumber: '',
    emailAddress: '',
    
    // Collection Details
    collectionAddress: '',
    collectionPostcode: '',
    collectionContactName: '',
    collectionDate: '',
    collectionTime: '',
    collectionFlexible: 'no',
    
    // Delivery Details
    deliveryAddress: '',
    deliveryPostcode: '',
    deliveryContactName: '',
    deliveryDate: '',
    deliveryTime: '',
    deliveryFlexible: 'no',
    
    // Shipment Information
    typeOfGoods: '',
    quantity: '',
    dimensions: '',
    weight: '',
    isFragile: 'no',
    requiresTempControl: 'no',
    requiresTwoMan: 'no',
    
    // Vehicle Requirement
    vehicleType: '',
    
    // Service Type
    serviceType: '',
    
    // Additional Options
    realtimeTracking: 'yes',
    insuranceRequired: 'no',
    specialInstructions: ''
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: '', message: '' });

    // Encode form data for Netlify
    const encode = (data) => {
      return Object.keys(data)
        .map(key => encodeURIComponent(key) + '=' + encodeURIComponent(data[key]))
        .join('&');
    };

    try {
      // Submit to Netlify Forms
      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: encode({
          'form-name': 'quote',
          ...formData
        })
      });

      if (response.ok) {
        setStatus({ 
          type: 'success', 
          message: 'Thank you for your enquiry. A member of our team will review your request and get back to you shortly.' 
        });
        setSubmitted(true);
        // Reset form
        setFormData({
          fullName: '', companyName: '', phoneNumber: '', emailAddress: '',
          collectionAddress: '', collectionPostcode: '', collectionContactName: '', collectionDate: '', collectionTime: '', collectionFlexible: 'no',
          deliveryAddress: '', deliveryPostcode: '', deliveryContactName: '', deliveryDate: '', deliveryTime: '', deliveryFlexible: 'no',
          typeOfGoods: '', quantity: '', dimensions: '', weight: '', isFragile: 'no', requiresTempControl: 'no', requiresTwoMan: 'no',
          vehicleType: '', serviceType: '',
          realtimeTracking: 'yes', insuranceRequired: 'no', specialInstructions: ''
        });
        
        // Scroll to top to show success message
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      // Fallback to API if Netlify Forms fails
      try {
        await api.post('/api/quote', formData);
        setStatus({ 
          type: 'success', 
          message: 'Thank you for your enquiry. A member of our team will review your request and get back to you shortly.' 
        });
        setSubmitted(true);
        // Reset form
        setFormData({
          fullName: '', companyName: '', phoneNumber: '', emailAddress: '',
          collectionAddress: '', collectionPostcode: '', collectionContactName: '', collectionDate: '', collectionTime: '', collectionFlexible: 'no',
          deliveryAddress: '', deliveryPostcode: '', deliveryContactName: '', deliveryDate: '', deliveryTime: '', deliveryFlexible: 'no',
          typeOfGoods: '', quantity: '', dimensions: '', weight: '', isFragile: 'no', requiresTempControl: 'no', requiresTwoMan: 'no',
          vehicleType: '', serviceType: '',
          realtimeTracking: 'yes', insuranceRequired: 'no', specialInstructions: ''
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (apiError) {
        setStatus({ type: 'error', message: 'Failed to submit quote request. Please try again or call us at 07502109790.' });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="quote-page">
      <section className="quote-hero">
        <div className="container">
          <h1>Get a Quote</h1>
          <p>Fill out the form below for a personalized quote from V&R Courier Ltd</p>
        </div>
      </section>

      <section className="quote-content">
        <div className="container">
          {status.message && (
            <div className={`status-message-top ${status.type}`}>
              {status.message}
            </div>
          )}

          <form 
            name="quote" 
            method="POST" 
            data-netlify="true" 
            netlify-honeypot="bot-field"
            className="quote-form" 
            onSubmit={handleSubmit}
          >
            <input type="hidden" name="form-name" value="quote" />
            <input type="hidden" name="bot-field" />
            {/* Contact Details */}
            <div className="form-section">
              <h2>Contact Details</h2>
              <div className="form-grid">
                <div className="form-group">
                  <label>Full Name</label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    placeholder="John Doe"
                  />
                </div>
                <div className="form-group">
                  <label>Company Name (optional)</label>
                  <input
                    type="text"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    placeholder="ABC Ltd"
                  />
                </div>
                <div className="form-group">
                  <label>Phone Number</label>
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    placeholder="07123456789"
                  />
                </div>
                <div className="form-group">
                  <label>Email Address</label>
                  <input
                    type="email"
                    name="emailAddress"
                    value={formData.emailAddress}
                    onChange={handleChange}
                    placeholder="john@example.com"
                  />
                </div>
              </div>
            </div>

            {/* Collection Details */}
            <div className="form-section">
              <h2>Collection Details</h2>
              <div className="form-grid">
                <div className="form-group full-width">
                  <label>Collection Address</label>
                  <input
                    type="text"
                    name="collectionAddress"
                    value={formData.collectionAddress}
                    onChange={handleChange}
                    placeholder="123 High Street, London"
                  />
                </div>
                <div className="form-group">
                  <label>Postcode</label>
                  <input
                    type="text"
                    name="collectionPostcode"
                    value={formData.collectionPostcode}
                    onChange={handleChange}
                    placeholder="SW1A 1AA"
                  />
                </div>
                <div className="form-group">
                  <label>Contact Name at Collection</label>
                  <input
                    type="text"
                    name="collectionContactName"
                    value={formData.collectionContactName}
                    onChange={handleChange}
                    placeholder="Jane Smith"
                  />
                </div>
                <div className="form-group">
                  <label>Collection Date</label>
                  <input
                    type="date"
                    name="collectionDate"
                    value={formData.collectionDate}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>Collection Time</label>
                  <input
                    type="time"
                    name="collectionTime"
                    value={formData.collectionTime}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>Is Collection Time Flexible?</label>
                  <select
                    name="collectionFlexible"
                    value={formData.collectionFlexible}
                    onChange={handleChange}
                  >
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Delivery Details */}
            <div className="form-section">
              <h2>Delivery Details</h2>
              <div className="form-grid">
                <div className="form-group full-width">
                  <label>Delivery Address</label>
                  <input
                    type="text"
                    name="deliveryAddress"
                    value={formData.deliveryAddress}
                    onChange={handleChange}
                    placeholder="456 Park Lane, Manchester"
                  />
                </div>
                <div className="form-group">
                  <label>Postcode</label>
                  <input
                    type="text"
                    name="deliveryPostcode"
                    value={formData.deliveryPostcode}
                    onChange={handleChange}
                    placeholder="M1 1AA"
                  />
                </div>
                <div className="form-group">
                  <label>Contact Name at Delivery</label>
                  <input
                    type="text"
                    name="deliveryContactName"
                    value={formData.deliveryContactName}
                    onChange={handleChange}
                    placeholder="Bob Johnson"
                  />
                </div>
                <div className="form-group">
                  <label>Delivery Date</label>
                    <input
                    type="date"
                    name="deliveryDate"
                    value={formData.deliveryDate}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>Delivery Time</label>
                  <input
                    type="time"
                    name="deliveryTime"
                    value={formData.deliveryTime}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>Is Delivery Time Flexible?</label>
                  <select
                    name="deliveryFlexible"
                    value={formData.deliveryFlexible}
                    onChange={handleChange}
                  >
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Shipment Information */}
            <div className="form-section">
              <h2>Shipment Information</h2>
              <div className="form-grid">
                <div className="form-group full-width">
                  <label>Type of Goods</label>
                  <input
                    type="text"
                    name="typeOfGoods"
                    value={formData.typeOfGoods}
                    onChange={handleChange}
                    placeholder="e.g., parcels, pallets, furniture, machinery, food, documents"
                  />
                </div>
                <div className="form-group">
                  <label>Quantity / Number of Items</label>
                  <input
                    type="text"
                    name="quantity"
                    value={formData.quantity}
                    onChange={handleChange}
                    placeholder="e.g., 5 boxes"
                  />
                </div>
                <div className="form-group">
                  <label>Dimensions (L x W x H)</label>
                  <input
                    type="text"
                    name="dimensions"
                    value={formData.dimensions}
                    onChange={handleChange}
                    placeholder="e.g., 120cm x 80cm x 60cm (approximate if unknown)"
                  />
                </div>
                <div className="form-group">
                  <label>Weight</label>
                  <input
                    type="text"
                    name="weight"
                    value={formData.weight}
                    onChange={handleChange}
                    placeholder="e.g., 50kg (approximate if unknown)"
                  />
                </div>
                <div className="form-group">
                  <label>Is the Load Fragile?</label>
                  <select
                    name="isFragile"
                    value={formData.isFragile}
                    onChange={handleChange}
                  >
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Requires Temperature Control?</label>
                  <select
                    name="requiresTempControl"
                    value={formData.requiresTempControl}
                    onChange={handleChange}
                  >
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Requires Two-Man Handling?</label>
                  <select
                    name="requiresTwoMan"
                    value={formData.requiresTwoMan}
                    onChange={handleChange}
                  >
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Vehicle Requirement */}
            <div className="form-section">
              <h2>Vehicle Requirement</h2>
              <div className="form-grid">
                <div className="form-group full-width">
                  <label>Select Vehicle Type</label>
                  <select
                    name="vehicleType"
                    value={formData.vehicleType}
                    onChange={handleChange}
                  >
                    <option value="">-- Please Select --</option>
                    <option value="small-van">Small Van</option>
                    <option value="medium-van">Medium Van</option>
                    <option value="lwb-van">Long Wheel Base Van</option>
                    <option value="xlwb-van">Extra Long Wheel Base Van</option>
                    <option value="luton-van">Luton Van</option>
                    <option value="box-van">Box Van</option>
                    <option value="curtain-side">Curtain-Side Van</option>
                    <option value="not-sure">Not Sure / Need Advice</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Service Type */}
            <div className="form-section">
              <h2>Service Type</h2>
              <div className="form-grid">
                <div className="form-group full-width">
                  <label>Select Service Type</label>
                  <select
                    name="serviceType"
                    value={formData.serviceType}
                    onChange={handleChange}
                  >
                    <option value="">-- Please Select --</option>
                    <option value="same-day">Same Day Delivery</option>
                    <option value="scheduled">Scheduled / Planned Delivery</option>
                    <option value="half-day-hire">Half Day Hire</option>
                    <option value="full-day-hire">Full Day Hire</option>
                    <option value="multi-drop">Multi-Drop Delivery</option>
                    <option value="house-move">House Move</option>
                    <option value="import-export">Import / Export / Freight Handling</option>
                    <option value="europe-transport">Europe Road Transport</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Additional Options */}
            <div className="form-section">
              <h2>Additional Options</h2>
              <div className="form-grid">
                <div className="form-group">
                  <label>Real-Time Tracking Required?</label>
                  <select
                    name="realtimeTracking"
                    value={formData.realtimeTracking}
                    onChange={handleChange}
                  >
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Insurance Documentation Required?</label>
                  <select
                    name="insuranceRequired"
                    value={formData.insuranceRequired}
                    onChange={handleChange}
                  >
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                </div>
                <div className="form-group full-width">
                  <label>Any Special Instructions?</label>
                  <textarea
                    name="specialInstructions"
                    value={formData.specialInstructions}
                    onChange={handleChange}
                    rows="4"
                    placeholder="Please provide any additional information that might help us serve you better..."
                  ></textarea>
                </div>
              </div>
            </div>

            <button type="submit" className="btn btn-primary btn-large" disabled={loading}>
              {loading ? 'Submitting...' : 'Submit Quote Request'}
            </button>
          </form>
        </div>
      </section>
    </main>
  );
}

export default GetQuote;
